import React, { useState, useEffect } from 'react';
import {
  Shield,
  AlertTriangle,
  Check,
  X,
  RefreshCw,
  Database,
  Upload,
  Clock,
  Key,
  Settings,
  Save,
  Eye,
  EyeOff
} from 'lucide-react';
import api from '../api';
import toast from 'react-hot-toast';

interface SecuritySetting {
  value: any;
  type: string;
  description: string;
  is_sensitive: boolean;
  updated_at: string;
}

interface SecuritySettings {
  [key: string]: SecuritySetting;
}

interface CategorySettings {
  [key: string]: SecuritySettings;
  antivirus: SecuritySettings;
  retention: SecuritySettings;
  security: SecuritySettings;
  uploads: SecuritySettings;
  compliance: SecuritySettings;
  session: SecuritySettings;
}

const SystemSecuritySettings: React.FC = () => {
  const [settings, setSettings] = useState<CategorySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSensitive, setShowSensitive] = useState<Record<string, boolean>>({});
  const [pendingChanges, setPendingChanges] = useState<Record<string, any>>({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/super-admin/security');
      setSettings(response.data.settings);
    } catch (error) {
      console.error('Failed to load security settings:', error);
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key: string, value: any) => {
    try {
      setSaving(true);
      await api.put(`/api/super-admin/security/setting/${key}`, { value });
      
      // Update local state
      if (settings) {
        const updatedSettings = { ...settings };
        Object.keys(updatedSettings).forEach(category => {
          const categorySettings = updatedSettings[category as keyof CategorySettings];
          if (categorySettings && categorySettings[key]) {
            categorySettings[key].value = value;
            categorySettings[key].updated_at = new Date().toISOString();
          }
        });
        setSettings(updatedSettings);
      }

      // Remove from pending changes
      const newPending = { ...pendingChanges };
      delete newPending[key];
      setPendingChanges(newPending);

      toast.success('Setting updated successfully');
    } catch (error) {
      console.error('Failed to update setting:', error);
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (key: string, value: any, type: string) => {
    let convertedValue = value;
    
    if (type === 'boolean') {
      convertedValue = value === 'true' || value === true;
    } else if (type === 'number') {
      convertedValue = parseInt(value) || 0;
    }

    setPendingChanges(prev => ({
      ...prev,
      [key]: convertedValue
    }));
  };

  const toggleSensitiveVisibility = (key: string) => {
    setShowSensitive(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const renderSettingInput = (key: string, setting: SecuritySetting, category: string) => {
    const currentValue = pendingChanges[key] !== undefined ? pendingChanges[key] : setting.value;
    const hasChanges = pendingChanges[key] !== undefined;

    // Define select options for specific settings
    const selectOptions: Record<string, { value: string; label: string }[]> = {
      'ANTIVIRUS_PROVIDER': [
        { value: 'heuristic', label: 'Heuristic (Built-in)' },
        { value: 'virustotal', label: 'VirusTotal API' },
        { value: 'cloudmersive', label: 'Cloudmersive API' },
        { value: 'clamav', label: 'ClamAV (Local)' }
      ],
      'COMPLIANCE_MODE': [
        { value: 'standard', label: 'Standard' },
        { value: 'sox', label: 'SOX Compliance' },
        { value: 'hipaa', label: 'HIPAA Compliance' },
        { value: 'gdpr', label: 'GDPR Compliance' },
        { value: 'pci', label: 'PCI-DSS Compliance' }
      ],
      'COMPANY_SIZE': [
        { value: '1-10', label: '1-10 employees' },
        { value: '11-50', label: '11-50 employees' },
        { value: '51-200', label: '51-200 employees' },
        { value: '201-500', label: '201-500 employees' },
        { value: '500+', label: '500+ employees' }
      ]
    };

    // Check if this setting should be a select dropdown
    if (selectOptions[key]) {
      return (
        <div className="flex items-center space-x-2">
          <select
            value={currentValue || ''}
            onChange={(e) => handleInputChange(key, e.target.value, setting.type)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {selectOptions[key].map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {hasChanges && (
            <button
              onClick={() => updateSetting(key, currentValue)}
              disabled={saving}
              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
            >
              <Save className="w-3 h-3" />
            </button>
          )}
        </div>
      );
    }

    if (setting.type === 'boolean') {
      return (
        <div className="flex items-center space-x-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={currentValue}
              onChange={(e) => handleInputChange(key, e.target.checked, setting.type)}
              className="sr-only"
            />
            <div className={`w-11 h-6 rounded-full ${currentValue ? 'bg-blue-600' : 'bg-gray-200'} relative transition-colors`}>
              <div className={`w-5 h-5 bg-white rounded-full transition-transform absolute top-0.5 ${
                currentValue ? 'translate-x-5' : 'translate-x-0.5'
              }`} />
            </div>
          </label>
          {hasChanges && (
            <button
              onClick={() => updateSetting(key, currentValue)}
              disabled={saving}
              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
            >
              <Save className="w-3 h-3" />
            </button>
          )}
        </div>
      );
    }

    if (setting.is_sensitive) {
      // Get provider-specific placeholder for API keys
      const getApiKeyPlaceholder = (key: string) => {
        if (key === 'VIRUSTOTAL_API_KEY') {
          return currentValue ? '••••••••••••••••' : 'Enter VirusTotal API key';
        } else if (key === 'CLOUDMERSIVE_API_KEY') {
          return currentValue ? '••••••••••••••••' : 'Enter Cloudmersive API key';
        }
        return 'Enter API key or sensitive value';
      };

      const placeholder = getApiKeyPlaceholder(key);

      return (
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <input
              type={showSensitive[key] ? 'text' : 'password'}
              value={currentValue || ''}
              onChange={(e) => handleInputChange(key, e.target.value, setting.type)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={placeholder}
            />
            {currentValue && !showSensitive[key] && (
              <div className="absolute right-10 top-2 text-xs text-green-600">
                ✓ Saved
              </div>
            )}
          </div>
          <button
            onClick={() => toggleSensitiveVisibility(key)}
            className="p-2 text-gray-500 hover:text-gray-700"
          >
            {showSensitive[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          {hasChanges && (
            <button
              onClick={() => updateSetting(key, currentValue)}
              disabled={saving}
              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
            >
              <Save className="w-3 h-3" />
            </button>
          )}
        </div>
      );
    }

    // Special handling for comma-separated lists (like file types)
    if (key === 'ALLOWED_FILE_TYPES') {
      return (
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={currentValue || ''}
            onChange={(e) => handleInputChange(key, e.target.value, setting.type)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="pdf,doc,docx,txt,xlsx,xls,ppt,pptx"
          />
          {hasChanges && (
            <button
              onClick={() => updateSetting(key, currentValue)}
              disabled={saving}
              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
            >
              <Save className="w-3 h-3" />
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-2">
        <input
          type={setting.type === 'number' ? 'number' : 'text'}
          value={currentValue || ''}
          onChange={(e) => handleInputChange(key, e.target.value, setting.type)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          min={setting.type === 'number' ? 0 : undefined}
        />
        {hasChanges && (
          <button
            onClick={() => updateSetting(key, currentValue)}
            disabled={saving}
            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
          >
            <Save className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'antivirus': return <Shield className="w-5 h-5 text-green-600" />;
      case 'retention': return <Database className="w-5 h-5 text-blue-600" />;
      case 'security': return <Key className="w-5 h-5 text-red-600" />;
      case 'uploads': return <Upload className="w-5 h-5 text-purple-600" />;
      case 'compliance': return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'session': return <Clock className="w-5 h-5 text-indigo-600" />;
      default: return <Settings className="w-5 h-5 text-gray-600" />;
    }
  };

  const getCategoryTitle = (category: string) => {
    switch (category) {
      case 'antivirus': return 'Antivirus & Malware Protection';
      case 'retention': return 'Data Retention Policies';
      case 'security': return 'Security Headers & Controls';
      case 'uploads': return 'File Upload Security';
      case 'compliance': return 'Compliance Settings';
      case 'session': return 'Session Management';
      default: return category;
    }
  };

  const testAntivirus = async () => {
    try {
      setSaving(true);
      const response = await api.post('/api/super-admin/security/test/antivirus');
      toast.success('Antivirus test completed successfully');
      console.log('Antivirus test result:', response.data);
    } catch (error) {
      console.error('Antivirus test failed:', error);
      toast.error('Antivirus test failed');
    } finally {
      setSaving(false);
    }
  };

  const executeRetention = async () => {
    try {
      setSaving(true);
      const response = await api.post('/api/super-admin/security/retention/execute');
      toast.success('Data retention policies executed');
      console.log('Retention result:', response.data);
    } catch (error) {
      console.error('Retention execution failed:', error);
      toast.error('Failed to execute retention policies');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading security settings...</span>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-gray-600">Failed to load security settings</p>
        <button
          onClick={loadSettings}
          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const renderAntivirusSection = () => {
    if (!settings?.antivirus) return null;
    
    const antivirusSettings = settings.antivirus;
    const currentProvider = pendingChanges['ANTIVIRUS_PROVIDER'] !== undefined 
      ? pendingChanges['ANTIVIRUS_PROVIDER'] 
      : antivirusSettings['ANTIVIRUS_PROVIDER']?.value || 'heuristic';
    
    const enabledValue = pendingChanges['ENABLE_ANTIVIRUS'] !== undefined 
      ? pendingChanges['ENABLE_ANTIVIRUS'] 
      : antivirusSettings['ENABLE_ANTIVIRUS']?.value;

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-green-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-green-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Antivirus Protection</h3>
                <p className="text-sm text-gray-600">Configure malware scanning for uploaded files</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                enabledValue ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {enabledValue ? 'Active' : 'Disabled'}
              </span>
              <button
                onClick={testAntivirus}
                disabled={saving || !enabledValue}
                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Test Scanner
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Enable Antivirus Scanning</h4>
              <p className="text-sm text-gray-600">Scan all uploaded files for malware and viruses</p>
            </div>
            {renderSettingInput('ENABLE_ANTIVIRUS', antivirusSettings['ENABLE_ANTIVIRUS'], 'antivirus')}
          </div>

          {enabledValue && (
            <>
              {/* Provider Selection */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Scanning Provider</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { 
                      value: 'heuristic', 
                      label: 'Built-in Heuristic', 
                      description: 'Fast, local scanning using pattern detection',
                      icon: '🔍',
                      recommended: false
                    },
                    { 
                      value: 'virustotal', 
                      label: 'VirusTotal', 
                      description: 'Cloud-based scanning with 70+ antivirus engines',
                      icon: '🌐',
                      recommended: true
                    },
                    { 
                      value: 'cloudmersive', 
                      label: 'Cloudmersive', 
                      description: 'Enterprise cloud scanning with advanced threat detection',
                      icon: '☁️',
                      recommended: false
                    },
                    { 
                      value: 'clamav', 
                      label: 'ClamAV', 
                      description: 'Local open-source antivirus engine',
                      icon: '🛡️',
                      recommended: false
                    }
                  ].map((provider) => (
                    <div 
                      key={provider.value}
                      className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        currentProvider === provider.value
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleInputChange('ANTIVIRUS_PROVIDER', provider.value, 'string')}
                    >
                      {provider.recommended && (
                        <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                          Recommended
                        </span>
                      )}
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">{provider.icon}</span>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="antivirus_provider"
                              value={provider.value}
                              checked={currentProvider === provider.value}
                              onChange={() => {}}
                              className="text-green-600 focus:ring-green-500"
                            />
                            <h5 className="font-medium text-gray-900">{provider.label}</h5>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{provider.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {pendingChanges['ANTIVIRUS_PROVIDER'] && (
                  <div className="flex justify-end">
                    <button
                      onClick={() => updateSetting('ANTIVIRUS_PROVIDER', pendingChanges['ANTIVIRUS_PROVIDER'])}
                      disabled={saving}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      <Save className="w-4 h-4 inline mr-2" />
                      Save Provider
                    </button>
                  </div>
                )}
              </div>

              {/* API Key Configuration - Show all API keys */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="font-medium text-gray-900 mb-4">API Configuration</h4>
                <div className="space-y-4">
                  {/* VirusTotal API Key */}
                  <div className={`border-2 rounded-lg p-4 transition-all ${
                    currentProvider === 'virustotal' 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <div className="flex items-center space-x-2">
                        <Key className={`w-5 h-5 mt-0.5 ${
                          currentProvider === 'virustotal' ? 'text-green-600' : 'text-gray-400'
                        }`} />
                        {currentProvider === 'virustotal' && (
                          <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                            Active
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <h5 className={`font-medium mb-1 ${
                          currentProvider === 'virustotal' ? 'text-green-900' : 'text-gray-600'
                        }`}>
                          VirusTotal API Key
                        </h5>
                        <p className={`text-sm mb-3 ${
                          currentProvider === 'virustotal' ? 'text-green-700' : 'text-gray-500'
                        }`}>
                          Get your free API key from{' '}
                          <a href="https://virustotal.com/gui/join-us" target="_blank" rel="noopener noreferrer" 
                             className="underline hover:no-underline">
                            virustotal.com/gui/join-us
                          </a>
                        </p>
                        <div className="space-y-2">
                          {renderSettingInput('VIRUSTOTAL_API_KEY', antivirusSettings['VIRUSTOTAL_API_KEY'], 'antivirus')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cloudmersive API Key */}
                  <div className={`border-2 rounded-lg p-4 transition-all ${
                    currentProvider === 'cloudmersive' 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <div className="flex items-center space-x-2">
                        <Key className={`w-5 h-5 mt-0.5 ${
                          currentProvider === 'cloudmersive' ? 'text-green-600' : 'text-gray-400'
                        }`} />
                        {currentProvider === 'cloudmersive' && (
                          <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                            Active
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <h5 className={`font-medium mb-1 ${
                          currentProvider === 'cloudmersive' ? 'text-green-900' : 'text-gray-600'
                        }`}>
                          Cloudmersive API Key
                        </h5>
                        <p className={`text-sm mb-3 ${
                          currentProvider === 'cloudmersive' ? 'text-green-700' : 'text-gray-500'
                        }`}>
                          Get your API key from{' '}
                          <a href="https://account.cloudmersive.com" target="_blank" rel="noopener noreferrer" 
                             className="underline hover:no-underline">
                            account.cloudmersive.com
                          </a>
                        </p>
                        <div className="space-y-2">
                          {renderSettingInput('CLOUDMERSIVE_API_KEY', antivirusSettings['CLOUDMERSIVE_API_KEY'], 'antivirus')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Info for non-API providers */}
                  {currentProvider !== 'virustotal' && currentProvider !== 'cloudmersive' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <Key className="w-5 h-5 text-blue-600" />
                        <div>
                          <h5 className="font-medium text-blue-900 mb-1">No API Key Required</h5>
                          <p className="text-sm text-blue-700">
                            The {currentProvider === 'heuristic' ? 'Built-in Heuristic' : 'ClamAV'} provider 
                            doesn't require an API key. Your saved API keys above will be preserved for future use.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Advanced Settings */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="font-medium text-gray-900 mb-4">Advanced Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Maximum Scan Size (MB)
                    </label>
                    <p className="text-xs text-gray-600 mb-2">
                      Files larger than this size won't be scanned
                    </p>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={Math.round((pendingChanges['MAX_FILE_SCAN_SIZE'] !== undefined 
                          ? pendingChanges['MAX_FILE_SCAN_SIZE'] 
                          : antivirusSettings['MAX_FILE_SCAN_SIZE']?.value || 52428800) / (1024 * 1024))}
                        onChange={(e) => handleInputChange('MAX_FILE_SCAN_SIZE', parseInt(e.target.value) * 1024 * 1024, 'number')}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min={1}
                        max={1024}
                      />
                      <span className="text-sm text-gray-500">MB</span>
                      {pendingChanges['MAX_FILE_SCAN_SIZE'] !== undefined && (
                        <button
                          onClick={() => updateSetting('MAX_FILE_SCAN_SIZE', pendingChanges['MAX_FILE_SCAN_SIZE'])}
                          disabled={saving}
                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                        >
                          <Save className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderOtherCategories = () => {
    return Object.entries(settings)
      .filter(([category]) => category !== 'antivirus')
      .map(([category, categorySettings]: [string, SecuritySettings]) => (
        <div key={category} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {getCategoryIcon(category)}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {getCategoryTitle(category)}
                  </h3>
                </div>
              </div>
              {category === 'retention' && (
                <button
                  onClick={executeRetention}
                  disabled={saving}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  Execute Now
                </button>
              )}
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {Object.entries(categorySettings).map(([key, setting]: [string, SecuritySetting]) => (
                <div key={key} className="p-4 border border-gray-200 rounded-lg">
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-900">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </label>
                      {setting.is_sensitive && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">
                          Sensitive
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600">{setting.description}</p>
                  </div>
                  
                  {renderSettingInput(key, setting, category)}
                  
                  <div className="mt-2 text-xs text-gray-500">
                    Last updated: {setting.updated_at ? new Date(setting.updated_at).toLocaleString() : 'Never'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ));
  };

  return (
    <div className="space-y-6">
      {renderAntivirusSection()}
      {renderOtherCategories()}

      {Object.keys(pendingChanges).length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800 font-medium">
              You have {Object.keys(pendingChanges).length} unsaved changes
            </span>
          </div>
          <p className="text-yellow-700 text-sm mt-1">
            Click the save button next to each changed setting to apply the changes.
          </p>
        </div>
      )}
    </div>
  );
};

export default SystemSecuritySettings;